import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import Input from '@/components/Input/Input';
import { HStack, Spacer, VStack } from '@/components/Layout';
import AddStepperNavigation from '@/components/Navigation/StepperNavigation';
import AddStepperStepTitleCardComponent from '@/components/Stepper/AddStepperStepTitleCard';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import { Text } from '@/components/Typography/Text';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { formatISODate } from '@/utils/date';
import { BaseDateWarning } from '../-components/BaseDateWarning';
import { useDepartmentRestructuringsQuery } from '../-query';
import { findLostApprovedPlans } from '../-utils/findLostApprovedPlans';
import { useStartOrganizationPlan } from './-query';
export const Route = createFileRoute('/_staff/organization/plan/create/')({
  component: RouteComponent
});
function RouteComponent() {
  const [name, setName] = useState('');
  const [applyDate, setApplyDate] = useState<Date | undefined>(undefined);
  const [baseDate, setBaseDate] = useState<Date | undefined>(undefined);
  // 基準日をユーザーがまだ一度も触っていない間は、適用日の変更に追随させる
  const [isBaseDateDirty, setIsBaseDateDirty] = useState(false);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.organization.plan.create'
  });
  const navigate = useNavigate();
  const {
    mutateAsync,
    isPending
  } = useStartOrganizationPlan();
  const plansQuery = useDepartmentRestructuringsQuery();
  const handleApplyDateChange = (d: Date | undefined) => {
    // 適用日・基準日は必須。Calendar の再クリック解除は受け付けない。
    if (!d) return;
    setApplyDate(d);
    if (!isBaseDateDirty) setBaseDate(d);
  };
  const handleBaseDateChange = (d: Date | undefined) => {
    if (!d) return;
    setIsBaseDateDirty(true);
    setBaseDate(d);
    // 基準日を先に遠い未来へ入れた場合、適用日が未設定 or 基準日より前だと
    // fromDate 制約で選択不能になるため、適用日を基準日以上に揃える。
    if (!applyDate || formatISODate(applyDate) < formatISODate(d)) {
      setApplyDate(d);
    }
  };

  // 基準日より後に適用予定の確定済み改編が、このプランの適用時に失われないかを警告する。
  // 作成前なので自分自身のプラン ID はまだ存在せず、除外は不要。
  const lostApprovedPlans = useMemo(() => {
    if (!baseDate || !applyDate || !plansQuery.data) return [];
    return findLostApprovedPlans({
      baseDate: formatISODate(baseDate),
      applyPlanDate: formatISODate(applyDate),
      plans: plansQuery.data
    });
  }, [baseDate, applyDate, plansQuery.data]);
  const handleSubmit = async () => {
    if (!name || !applyDate || !baseDate) return;
    await mutateAsync({
      name,
      applyPlanDate: formatISODate(applyDate),
      baseDate: formatISODate(baseDate)
      // NOTE: Descriptionの入力ないので省略
    });
    // NOTE: 実行結果never型なので一覧に遷移
    navigate({
      to: '/organization/plan'
    });
  };
  const isDisabled = !name || !applyDate || !baseDate || isPending;
  return <HStack className="p-10">
      <AddStepperNavigation link={{
      hash: '1',
      to: '/organization/plan/add'
    }} title={t('title')} sections={[{
      label: t('steps.organizationPlanInfo'),
      hash: '1'
    }]} />
      <Spacer variant="l" />
      <VStack className="gap-6 min-w-[560px]">
        <AddStepperStepTitleCardComponent stepName={t('organizationPlanInfoStep.title')} />
        <VStack className="bg-neutral-fill p-6 gap-4">
          <VStack className="gap-2">
            <Text variant="label">{t('organizationPlanInfoStep.name')}</Text>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="-" />
          </VStack>
          <VStack className="gap-2">
            <Text variant="label">
              {t('organizationPlanInfoStep.effectiveDate')}
            </Text>
            {/* 適用日は基準日以降（BE: ApplyPlanDateMustBeAfterBaseDate） */}
            <SimpleDatePicker value={applyDate} onChange={handleApplyDateChange} disablePast fromDate={baseDate} />
            <Text variant="pSmall" className="text-neutral-muted-on-fill">
              {t('organizationPlanInfoStep.effectiveDateDescription')}
            </Text>
          </VStack>
          <VStack className="gap-2">
            <Text variant="label">
              {t('organizationPlanInfoStep.baseDate')}
            </Text>
            {/* 基準日は今日以降かつ適用日以前
                （BE: BaseDateMustBeFuture / ApplyPlanDateMustBeAfterBaseDate） */}
            <SimpleDatePicker value={baseDate} onChange={handleBaseDateChange} disablePast toDate={applyDate} />
            <Text variant="pSmall" className="text-neutral-muted-on-fill">
              {t('organizationPlanInfoStep.baseDateDescription')}
            </Text>
          </VStack>
        </VStack>
        <BaseDateWarning plans={lostApprovedPlans} />
        <div className="mb-32">
          <DockAddStepperAction baseLink="/organization/plan/add" previousPage={null} nextPage={null} nextComplete disabled={isDisabled} onNextClick={handleSubmit} onCompleteClick={handleSubmit} />
        </div>
      </VStack>
    </HStack>;
}